/**
 * Handwriting send flow: off-the-shelf catalog + signed-in user's `handwriting_stationery` row.
 */
import { useHandwrittenNoteFormStore } from '~~/layers/base/stores/handwrittenNoteForm.store'
import { useApiBaseUrl } from '~~/layers/base/composables/useApiBaseUrl'
import { useBetterAuth } from '~~/layers/base/composables/useBetterAuth'
import {
    resolveAuthSessionKey,
    type AuthHydrateSessionKey,
    type AuthSessionSnapshot,
} from '~~/layers/base/utils/authSessionKey'
import {
    clearHasCustomStationeryCache,
    readHasCustomStationeryCache,
    writeHasCustomStationeryCache,
} from '~~/layers/base/utils/hasCustomStationeryCache'
import { mergeCapacitorDesignerFetchOptions } from '~~/layers/base/utils/mergeCapacitorDesignerFetchOptions'
import { MY_HANDWRITING_STATIONERY_GRID_ID } from '~~/layers/base/constants/customizeStationeryGridPreview'
import {
    isStationeryPreviewCardFront,
    resolveStationeryPreviewDeliveryUrl,
} from '~~/layers/base/utils/stationeryPreviewImage'
import { cloudflareScrollingGridThumbnailUrl } from '~~/layers/base/utils/cloudflareScrollingGridThumbnailUrl'
import { stationeryPreviewAspectWidthOverHeightFromMeta } from '~~/layers/base/utils/stationeryPreviewAspectFromMeta'

export type HandwritingCatalogPreviewEntry = {
    label: string
    url: string | null
    meta: Record<string, unknown>
}

export type HandwritingCatalogItem = {
    id: string
    designProjectId: string
    warehouseId: string
    sku: string | null
    name: string | null
    label: string
    carouselImageUrl: string | null
    previewImageUrls: string[]
    previewEntries: HandwritingCatalogPreviewEntry[]
    primaryPreviewMeta: Record<string, unknown>
    /** Stored OTS paper-swatch fill hex (`#RRGGBB`). */
    swatchColor: string | null
    /** Stored OTS painted-border hex; null when solid paper only. */
    swatchAccentColor: string | null
}

export type UserHandwritingCatalogItem = {
    handwritingStationeryId: string
    /** Null for concierge stationery (TK-13787). */
    designProjectId: string | null
    label: string
    carouselImageUrl: string | null
    previewImageUrls: string[]
    previewEntries: HandwritingCatalogPreviewEntry[]
    returnAddress: {
        id: string
        prefix: string | null
        first_name: string | null
        last_name: string | null
        suffix: string | null
        raw_address_str: string | null
        street_address_1: string | null
        street_address_2: string | null
        city: string | null
        state: string | null
        zip_code: string | null
        country: string | null
    } | null
    primaryPreviewMeta: Record<string, unknown>
}

/**
 * Read `surfaceId` from mixed API shapes (`surfaceId` / `surface_id`, string/number).
 * Needed so tile stack selection does not depend on volatile label text.
 */
function metaSurfaceIdNum(meta: Record<string, unknown>): number | undefined {
    const m = meta as { surfaceId?: unknown; surface_id?: unknown }
    const raw = m.surfaceId ?? m.surface_id
    if (typeof raw === 'number' && Number.isFinite(raw)) return raw
    if (typeof raw === 'string' && raw.trim() !== '') {
        const n = Number(raw)
        if (Number.isFinite(n)) return n
    }
    return undefined
}

function previewLabelIsLiner(label: string | undefined): boolean {
    const normalized = label?.toLowerCase().trim() ?? ''
    return normalized === 'liner' || normalized.includes('liner')
}

/**
 * Best-effort liner row selection for stack previews.
 * Prefer explicit liner naming first, then fallback to surface id matching.
 */
function stackLinerPreviewUrl(entries: HandwritingCatalogPreviewEntry[]): string | null {
    const explicitLiner = entries.find((e) => {
        const partName = String((e.meta as { partName?: string }).partName ?? '').toLowerCase()
        return Boolean(e.url) && (partName.includes('liner') || previewLabelIsLiner(e.label))
    })
    if (explicitLiner?.url) return explicitLiner.url

    const bySurface = entries.find((e) => {
        const sid = metaSurfaceIdNum(e.meta)
        const partName = String((e.meta as { partName?: string }).partName ?? '').toLowerCase()
        return sid === 4 && partName.includes('liner') && Boolean(e.url)
    })
    return bySurface?.url ?? null
}

/**
 * Card-front row selection for stack previews.
 * Excludes liner/envelope rows so the check overlay and stack alignment stay on the card face.
 */
function stackFrontPreviewUrl(entries: HandwritingCatalogPreviewEntry[]): string | null {
    return entries.find((e) => Boolean(e.url) && isStationeryPreviewCardFront(e))?.url ?? null
}

function aspectWidthOverHeightFromMeta(meta: Record<string, unknown> | undefined): number | null {
    if (!meta || typeof meta !== 'object') return null
    const canvas = meta.referenceCanvasPx as { width?: unknown; height?: unknown } | undefined
    if (
        canvas &&
        typeof canvas.width === 'number' &&
        typeof canvas.height === 'number' &&
        canvas.width > 0 &&
        canvas.height > 0
    ) {
        return canvas.width / canvas.height
    }
    return null
}

/**
 * Width÷height used for stacked tile frame sizing.
 * Chooses the smaller ratio when both layers exist so the tile reserves enough vertical room.
 */
function stackAspectWidthOverHeight(
    entries: HandwritingCatalogPreviewEntry[],
    fallback: number
): number {
    const front = entries.find((e) => {
        if (!e.url) return false
        const partName = String((e.meta as { partName?: string }).partName ?? '').toLowerCase()
        const label = e.label.toLowerCase()
        const sid = metaSurfaceIdNum(e.meta)
        return (
            sid === 1 &&
            !partName.includes('envelope') &&
            !partName.includes('liner') &&
            !label.includes('envelope') &&
            !label.includes('liner')
        )
    })
    const liner = entries.find((e) => {
        if (!e.url) return false
        const partName = String((e.meta as { partName?: string }).partName ?? '').toLowerCase()
        const sid = metaSurfaceIdNum(e.meta)
        return partName.includes('liner') || previewLabelIsLiner(e.label) || sid === 4
    })

    const frontRatio = front ? aspectWidthOverHeightFromMeta(front.meta) : null
    const linerRatio = liner ? aspectWidthOverHeightFromMeta(liner.meta) : null

    // Stacks should reserve enough height for whichever layer is taller.
    if (frontRatio && linerRatio) return Math.min(frontRatio, linerRatio)
    if (linerRatio) return linerRatio
    if (frontRatio) return frontRatio
    return fallback
}

type ApiPreview = {
    label: string
    cloudflare_image_id?: string
    external_url?: string
    meta: Record<string, unknown>
    url: string | null
}

type ApiOtsItem = {
    id: string
    warehouse_id: string
    design_project_id: string
    sku: string | null
    design_project_name: string | null
    preview_images: ApiPreview[]
    swatch_color?: string | null
    swatch_accent_color?: string | null
}

export type ApiUserHandwritingItem = {
    handwriting_stationery_id: string
    design_project_id: string | null
    is_active: boolean
    label: string
    carousel_image_url: string | null
    preview_image_urls: string[]
    preview_images?: ApiPreview[]
    return_address: {
        id: string
        prefix: string | null
        first_name: string | null
        last_name: string | null
        suffix: string | null
        raw_address_str: string | null
        street_address_1: string | null
        street_address_2: string | null
        city: string | null
        state: string | null
        zip_code: string | null
        country: string | null
    } | null
    primary_preview_meta?: Record<string, unknown>
}

export const useHandwritingCatalogStore = defineStore('handwritingCatalog', () => {
    const items = ref<HandwritingCatalogItem[]>([])
    const userHandwritingItem = ref<UserHandwritingCatalogItem | null>(null)
    const meHandwritingStationeryItem = ref<ApiUserHandwritingItem | null>(null)
    const loading = ref(false)
    const error = ref<string | null>(null)
    const contextIncludeUserHandwriting = ref(false)

    /**
     * SWR verdict for "this account has active custom stationery", seeded from device storage and
     * refreshed by every `/me` fetch. `null` means unknown — never resolved for this account.
     *
     * Deliberately survives `setSendFlowContext(false)`: on a cold start the session resolves after
     * the Send page mounts, so a guest-context flip must not erase a cached verdict the accordion
     * default depends on. Only explicit sign-out clears it.
     */
    const hasCustomStationery = ref<boolean | null>(null)

    const formStore = useHandwrittenNoteFormStore()
    const runtimeConfig = useRuntimeConfig() as unknown as {
        public: { cloudflareImagesBaseUrl?: string }
    }

    const selectedItem = computed<HandwritingCatalogItem | null>(() => {
        if (formStore.templateChoice !== 'preset' || !formStore.selectedTemplateId) return null
        return items.value.find((row) => row.id === formStore.selectedTemplateId) ?? null
    })

    const selectedExtraPreviewUrls = computed(() => {
        if (formStore.templateChoice === 'my_handwriting') {
            const u = userHandwritingItem.value
            if (!u?.previewImageUrls.length) return []
            return u.previewImageUrls.slice(1)
        }
        const row = selectedItem.value
        if (!row?.previewImageUrls.length) return []
        return row.previewImageUrls.slice(1)
    })

    const selectedPrimaryPreviewUrl = computed(() => {
        if (formStore.templateChoice === 'my_handwriting') {
            const mapped = userHandwritingItem.value?.carouselImageUrl
            if (mapped) return mapped
            const me = meHandwritingStationeryItem.value
            return me?.carousel_image_url ?? me?.preview_image_urls?.[0] ?? null
        }
        if (formStore.templateChoice === 'preset') {
            return selectedItem.value?.carouselImageUrl ?? null
        }
        return null
    })

    /**
     * Fixes stale **media-crane.com** `cdn-cgi/image/.../https://s3.../paper/alternate_*` shapes that sometimes
     * linger on `p.url` when `external_url` / resolver output was not authoritative.
     *
     * Those URLs embed the real HTTPS source after the path. Fabric/Capacitor needs the **embedded** `https://…`
     * URL: the outer media-crane response can advertise CORS for the web app origin while the inner S3/Crane URL
     * still fails CORS in the webview when used as the actual image request. Stripping to the inner URL aligns
     * with {@link resolveStationeryPreviewDeliveryUrl} and the native-fetch paper regex in handwritten / designer previews.
     *
     * Non-matching strings pass through unchanged (already-normal Cloudflare Images URLs, plain CDN links, etc.).
     */
    function normalizePreviewUrlForCapacitor(url: string | null | undefined): string | null {
        const raw = typeof url === 'string' ? url.trim() : ''
        if (!raw) return null
        const marker = '/https://s3.amazonaws.com/cdn.crane.com/paper/alternate_'
        if (raw.startsWith('https://media-crane.com/cdn-cgi/image/') && raw.includes(marker)) {
            const idx = raw.indexOf('/https://')
            if (idx !== -1) {
                return raw.slice(idx + 1)
            }
        }
        return raw
    }

    function mapOtsApiItem(row: ApiOtsItem): HandwritingCatalogItem {
        const previews = row.preview_images ?? []
        const cfBase = String(runtimeConfig.public.cloudflareImagesBaseUrl ?? '')
        const previewEntries: HandwritingCatalogPreviewEntry[] = previews.map((p) => ({
            label: p.label ?? '',
            url: normalizePreviewUrlForCapacitor(
                resolveStationeryPreviewDeliveryUrl(
                    { cloudflare_image_id: p.cloudflare_image_id, external_url: p.external_url },
                    cfBase
                ) ?? p.url
            ),
            meta:
                p.meta && typeof p.meta === 'object' && !Array.isArray(p.meta)
                    ? (p.meta as Record<string, unknown>)
                    : {},
        }))
        const urls = previewEntries.map((e) => e.url).filter((u): u is string => Boolean(u))
        const label = row.design_project_name?.trim() || row.sku?.trim() || 'Stationery'
        const primaryMeta = previews[0]?.meta
        return {
            id: row.id,
            designProjectId: row.design_project_id,
            warehouseId: row.warehouse_id,
            sku: row.sku,
            name: row.design_project_name,
            label,
            carouselImageUrl: urls[0] ?? null,
            previewImageUrls: urls,
            previewEntries,
            primaryPreviewMeta: primaryMeta && typeof primaryMeta === 'object' ? primaryMeta : {},
            swatchColor: row.swatch_color?.trim() || null,
            swatchAccentColor: row.swatch_accent_color?.trim() || null,
        }
    }

    function mapUserApiItem(row: ApiUserHandwritingItem): UserHandwritingCatalogItem {
        const urls = row.preview_image_urls ?? []
        const meta = row.primary_preview_meta
        const primaryPreviewMeta =
            meta && typeof meta === 'object' && !Array.isArray(meta) ? meta : {}
        const cfBase = String(runtimeConfig.public.cloudflareImagesBaseUrl ?? '')
        const previewEntries: HandwritingCatalogPreviewEntry[] = (row.preview_images ?? []).map(
            (p) => ({
                label: p.label ?? '',
                url: normalizePreviewUrlForCapacitor(
                    resolveStationeryPreviewDeliveryUrl(
                        {
                            cloudflare_image_id: p.cloudflare_image_id,
                            external_url: p.external_url,
                        },
                        cfBase
                    ) ?? p.url
                ),
                meta:
                    p.meta && typeof p.meta === 'object' && !Array.isArray(p.meta)
                        ? (p.meta as Record<string, unknown>)
                        : {},
            })
        )
        const derivedUrls = previewEntries.map((e) => e.url).filter((u): u is string => Boolean(u))
        return {
            handwritingStationeryId: row.handwriting_stationery_id,
            designProjectId: row.design_project_id,
            label: row.label,
            carouselImageUrl: derivedUrls[0] ?? row.carousel_image_url ?? urls[0] ?? null,
            previewImageUrls: derivedUrls.length ? derivedUrls : urls,
            previewEntries,
            returnAddress: row.return_address ?? null,
            primaryPreviewMeta,
        }
    }

    let sendFlowCatalogGeneration = 0
    let offShelfFetchInFlight: Promise<void> | null = null

    async function fetchOffShelfFromApi(): Promise<void> {
        if (offShelfFetchInFlight) return offShelfFetchInFlight
        offShelfFetchInFlight = (async () => {
            const apiBaseUrl = import.meta.client ? useApiBaseUrl() : undefined
            const res = (await $fetch(
                '/api/off_the_shelf_stationery',
                await mergeCapacitorDesignerFetchOptions({
                    ...(apiBaseUrl ? { baseURL: apiBaseUrl } : {}),
                })
            )) as { items?: ApiOtsItem[] }
            items.value = (res.items ?? []).map(mapOtsApiItem)
        })()
        try {
            await offShelfFetchInFlight
        } finally {
            offShelfFetchInFlight = null
        }
    }

    /**
     * Current account fingerprint for the custom-stationery cache. Read inside actions (not store
     * setup) so it reflects the session at call time, matching `profile.store`'s approach.
     */
    function currentAuthSessionKey(): AuthHydrateSessionKey {
        const { session, isLoggedIn } = useBetterAuth()
        return resolveAuthSessionKey(session.value as AuthSessionSnapshot, {}, isLoggedIn.value)
    }

    /**
     * Seeds {@link hasCustomStationery} from device storage so the Send flow can pick an accordion
     * default before `/me` resolves. Never overwrites a verdict already in memory: a completed
     * fetch is fresher than the cache it wrote.
     */
    async function hydrateHasCustomStationeryFromCache(): Promise<void> {
        if (!import.meta.client) return
        if (hasCustomStationery.value !== null) return
        const cached = await readHasCustomStationeryCache(currentAuthSessionKey())
        if (hasCustomStationery.value === null) {
            hasCustomStationery.value = cached
        }
    }

    /** Forgets the verdict in memory and on device. Call from logout while the session key resolves. */
    async function clearHasCustomStationery(): Promise<void> {
        const sessionKey = currentAuthSessionKey()
        hasCustomStationery.value = null
        await clearHasCustomStationeryCache(sessionKey)
    }

    async function fetchUserHandwritingFromApi(): Promise<void> {
        if (!contextIncludeUserHandwriting.value) return
        const apiBaseUrl = import.meta.client ? useApiBaseUrl() : undefined
        const data = (await $fetch(
            '/api/handwriting_stationery/me',
            await mergeCapacitorDesignerFetchOptions({
                ...(apiBaseUrl ? { baseURL: apiBaseUrl } : {}),
            })
        )) as { item?: ApiUserHandwritingItem | null }
        const raw = data.item ?? null
        meHandwritingStationeryItem.value = raw
        // Always map when `/me` returns a row so `returnAddress` and FK ids stay available even if preview
        // URLs are missing (grid tile still gated on `carouselImageUrl` in `stationeryGridOptions`).
        userHandwritingItem.value = raw ? mapUserApiItem(raw) : null

        // Only `is_active` rows are selectable in send flow, so an inactive row counts as "no custom
        // stationery" here. Written on success only — a failed revalidate keeps the cached verdict.
        const resolved = raw?.is_active === true
        hasCustomStationery.value = resolved
        void writeHasCustomStationeryCache(currentAuthSessionKey(), resolved)
    }

    async function loadSendFlowCatalog(options?: { quiet?: boolean }): Promise<void> {
        if (!import.meta.client) return
        const generation = ++sendFlowCatalogGeneration
        const quiet = options?.quiet === true
        if (!quiet) loading.value = true
        error.value = null

        const wantUser = contextIncludeUserHandwriting.value
        if (!wantUser) {
            userHandwritingItem.value = null
            meHandwritingStationeryItem.value = null
        }

        try {
            const ots = fetchOffShelfFromApi().catch((e) => {
                error.value = e instanceof Error ? e.message : String(e)
                items.value = []
            })
            const user = wantUser
                ? fetchUserHandwritingFromApi().catch(() => {
                      userHandwritingItem.value = null
                      meHandwritingStationeryItem.value = null
                  })
                : Promise.resolve()
            await Promise.all([ots, user])
        } finally {
            if (generation === sendFlowCatalogGeneration && !quiet) loading.value = false
        }
    }

    function setSendFlowContext(includeUserHandwriting: boolean) {
        contextIncludeUserHandwriting.value = includeUserHandwriting
        return loadSendFlowCatalog()
    }

    function reload() {
        return loadSendFlowCatalog({ quiet: true })
    }

    const stationeryGridOptions = computed(() => {
        const tiles: {
            id: string
            label: string
            /** Full `partPreview` URL — hero, Fabric, customize first-preset paper. */
            imageUrl: string | null
            thumbnailUrl: string | null
            stackFrontImageUrl: string | null
            stackLinerImageUrl: string | null
            gridAspectRatio: number
            stackGridAspectRatio: number
            /** My-stationery limbo state: row exists but `/me.is_active === false` (not selectable yet). */
            isInProduction?: boolean
            swatchColor?: string | null
            swatchAccentColor?: string | null
        }[] = []

        const u = userHandwritingItem.value
        if (u?.carouselImageUrl) {
            const full = u.carouselImageUrl
            const gridAspectRatio = stationeryPreviewAspectWidthOverHeightFromMeta(
                u.primaryPreviewMeta
            )
            tiles.push({
                id: MY_HANDWRITING_STATIONERY_GRID_ID,
                label: u.label,
                imageUrl: full,
                thumbnailUrl: cloudflareScrollingGridThumbnailUrl(full, gridAspectRatio),
                stackFrontImageUrl: stackFrontPreviewUrl(u.previewEntries),
                stackLinerImageUrl: stackLinerPreviewUrl(u.previewEntries),
                gridAspectRatio,
                stackGridAspectRatio: stackAspectWidthOverHeight(u.previewEntries, gridAspectRatio),
                // Carry "row exists but inactive" into tile UI for overlay + non-selectable behavior.
                isInProduction: meHandwritingStationeryItem.value?.is_active === false,
            })
        }

        for (const row of items.value) {
            const full = row.carouselImageUrl
            const gridAspectRatio = stationeryPreviewAspectWidthOverHeightFromMeta(
                row.primaryPreviewMeta
            )
            tiles.push({
                id: row.id,
                label: row.label,
                imageUrl: full,
                thumbnailUrl: full
                    ? cloudflareScrollingGridThumbnailUrl(full, gridAspectRatio)
                    : null,
                stackFrontImageUrl: stackFrontPreviewUrl(row.previewEntries),
                stackLinerImageUrl: stackLinerPreviewUrl(row.previewEntries),
                gridAspectRatio,
                stackGridAspectRatio: stackAspectWidthOverHeight(
                    row.previewEntries,
                    gridAspectRatio
                ),
                swatchColor: row.swatchColor,
                swatchAccentColor: row.swatchAccentColor,
            })
        }
        return tiles
    })

    return {
        items,
        userHandwritingItem,
        meHandwritingStationeryItem,
        loading,
        error,
        contextIncludeUserHandwriting,
        hasCustomStationery,
        hydrateHasCustomStationeryFromCache,
        clearHasCustomStationery,
        selectedItem,
        selectedExtraPreviewUrls,
        selectedPrimaryPreviewUrl,
        stationeryGridOptions,
        presetStationeryOptions: stationeryGridOptions,
        setSendFlowContext,
        reload,
    }
})
